import React, { useEffect, useState, useCallback } from 'react';
import AdminNav from '../../components/admin/AdminNav';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../utils/api';
import { FaSearch, FaEdit, FaTrash, FaInfoCircle } from 'react-icons/fa';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SupplierInfoModal from '../../components/common/SupplierInfoModal';

const SUPPLIERS_PER_PAGE = 5;

export default function AdminSupplierPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState({ street: '', city: '', state: '', zip: '' });
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [noLinkedSupplier, setNoLinkedSupplier] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const indexOfLastSupplier = currentPage * SUPPLIERS_PER_PAGE;
  const indexOfFirstSupplier = indexOfLastSupplier - SUPPLIERS_PER_PAGE;
  const currentSuppliers = filteredSuppliers.slice(indexOfFirstSupplier, indexOfLastSupplier);
  const totalPages = Math.ceil(filteredSuppliers.length / SUPPLIERS_PER_PAGE);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setNoLinkedSupplier(false);
    try {
      const res = await api.get('/api/suppliers/my-suppliers');
      const list = Array.isArray(res.data) ? res.data : [];
      setSuppliers(list);
      setFilteredSuppliers(list);
      setCurrentPage(1);
      if (list.length === 0) setNoLinkedSupplier(true);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
      if (err.response) {
        if (
          err.response.status === 404 &&
          err.response.data?.message === 'Associated supplier not found.'
        ) {
          toast.error('Your supplier-admin account isn’t linked to a valid supplier.');
          setSuppliers([]);
          setFilteredSuppliers([]);
          setNoLinkedSupplier(true);
        } else if (err.response.status === 403) {
          toast.error('Forbidden: insufficient privileges to view suppliers.');
        } else {
          toast.error('Failed to load suppliers.');
        }
      } else {
        toast.error('Network error while loading suppliers.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      const lower = query.toLowerCase();
      setFilteredSuppliers(
        suppliers.filter((s) => s.name?.toLowerCase().includes(lower))
      );
      setCurrentPage(1);
    },
    [suppliers]
  );

  useEffect(() => {
    if (searchQuery === '') setFilteredSuppliers(suppliers);
  }, [suppliers, searchQuery]);

  const handleAddressChange = (field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setAddress({ street: '', city: '', state: '', zip: '' });
    setEditingSupplier(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name, email, phone, address };
      if (editingSupplier) {
        const res = await api.put(`/api/suppliers/${editingSupplier._id}`, payload);
        const updated = res.data;
        toast.success(`Supplier "${updated.name}" updated!`);
        setSuppliers((prev) => prev.map((s) => (s._id === updated._id ? updated : s)));
        setFilteredSuppliers((prev) =>
          prev.map((s) => (s._id === updated._id ? updated : s))
        );
      } else {
        const res = await api.post('/api/suppliers', payload);
        const created = res.data;
        toast.success(`Supplier "${created.name}" added!`);
        setSuppliers((prev) => [created, ...prev]);
        setFilteredSuppliers((prev) => [created, ...prev]);
      }
      resetForm();
      fetchSuppliers();
    } catch (err) {
      console.error('Save supplier failed:', err);
      toast.error(err.response?.data?.message || 'Failed to save supplier.');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setName(supplier.name);
    setEmail(supplier.email || '');
    setPhone(supplier.phone || '');
    setAddress(supplier.address || { street: '', city: '', state: '', zip: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (supplier) => {
    setSupplierToDelete(supplier);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await api.delete(`/api/suppliers/${supplierToDelete._id}`);
      setSuppliers((prev) => prev.filter((s) => s._id !== supplierToDelete._id));
      setFilteredSuppliers((prev) => prev.filter((s) => s._id !== supplierToDelete._id));
      toast.success(`Supplier "${supplierToDelete.name}" deleted.`);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.message || 'Failed to delete supplier.');
    } finally {
      setShowConfirm(false);
      setSupplierToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setSupplierToDelete(null);
  };

  const openInfoModal = (supplier) => {
    setSelectedSupplier(supplier);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-5xl mx-auto bg-white shadow p-6 rounded-md">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <h2 className="text-2xl font-bold">Admin - Manage Suppliers</h2>
        </div>

        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-grow">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search suppliers by name"
              className="border p-2 w-full pr-10 rounded"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
              <FaSearch />
            </div>
          </div>
        </div>

        {noLinkedSupplier && suppliers.length === 0 && (
          <div className="mb-4 p-4 bg-yellow-100 border border-yellow-300 rounded">
            <p className="text-sm text-yellow-800">
              Your supplier-admin account isn’t linked to a valid supplier. Contact a superadmin to
              associate you with a supplier.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-2 mb-6">
          <h3 className="text-lg font-semibold">
            {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Supplier Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Supplier Name"
                className="border p-2 w-full rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone"
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Street</label>
              <input
                type="text"
                value={address.street || ''}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                placeholder="Street"
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City</label>
              <input
                type="text"
                value={address.city || ''}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                placeholder="City"
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">State</label>
              <input
                type="text"
                value={address.state || ''}
                onChange={(e) => handleAddressChange('state', e.target.value)}
                placeholder="State"
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zip</label>
              <input
                type="text"
                value={address.zip || ''}
                onChange={(e) => handleAddressChange('zip', e.target.value)}
                placeholder="Zip"
                className="border p-2 w-full rounded"
              />
            </div>
          </div>

          <div className="flex space-x-2 mt-2">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              disabled={loading}
            >
              {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
            </button>
            {editingSupplier && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <h3 className="text-lg font-semibold mb-2">Suppliers</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 mb-4">
            <thead>
              <tr className="bg-gray-200">
                <th className="border p-2 text-left">Name</th>
                <th className="border p-2 text-left">Email</th>
                <th className="border p-2 text-left">Phone</th>
                <th className="border p-2 text-left">Address</th>
                <th className="border p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="border p-4 text-center">
                    Loading suppliers...
                  </td>
                </tr>
              ) : currentSuppliers.length > 0 ? (
                currentSuppliers.map((supplier) => (
                  <tr key={supplier._id} className="text-center border-t hover:bg-gray-50">
                    <td className="border p-2 text-left">{supplier.name}</td>
                    <td className="border p-2 text-left">{supplier.email || '-'}</td>
                    <td className="border p-2 text-left">{supplier.phone || '-'}</td>
                    <td className="border p-2 text-left">
                      {supplier.address?.street}, {supplier.address?.city},{' '}
                      {supplier.address?.state} {supplier.address?.zip}
                    </td>
                    <td className="border p-2 space-x-2">
                      <button
                        onClick={() => openInfoModal(supplier)}
                        className="inline-flex items-center gap-1 bg-indigo-500 text-white px-3 py-1 rounded"
                      >
                        <FaInfoCircle />
                        Info
                      </button>
                      <button
                        onClick={() => handleEdit(supplier)}
                        className="inline-flex items-center gap-1 bg-yellow-500 text-white px-3 py-1 rounded"
                      >
                        <FaEdit />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(supplier)}
                        className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1 rounded"
                      >
                        <FaTrash />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="border p-4 text-center text-gray-600">
                    {suppliers.length === 0
                      ? 'No suppliers available.'
                      : 'No suppliers match the search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center space-x-2 mt-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === i + 1
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-800 hover:bg-gray-100'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        <SupplierInfoModal
          supplier={selectedSupplier}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />

        <ConfirmationModal
          isOpen={showConfirm}
          title="Confirm Delete"
          message={`Are you sure you want to delete supplier "${supplierToDelete?.name}"? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onClose={cancelDelete}
          username={supplierToDelete?.name}
        />
      </div>
    </div>
  );
}
